import gql from 'graphql-tag';
import { get, noop } from 'lodash';

import { useLazyQuery, useQuery } from '@apollo/react-hooks';

import { MODES as SIDEBAR_RIGHT_MODES } from 'app/developer-v3/components/SidebarRight';

import type { ValidationResultSetWrapper } from 'app/developer-v3/types';
import type { SemVer } from 'app/developer-v3/types/version';

type CheckViolationResultDetails = {
  message: string;
  tag: string;
  deepLink: string;
};

type CheckViolationResults = {
  type: string;
  violations: {
    type: string;
    results: CheckViolationResultDetails[];
  };
};

type CheckViolations = {
  totalFailures: number;
  results: CheckViolationResults[];
};

type QueryData = {
  platformIntegration: {
    id: ID;
    implementation: {
      id: ID;
      checks: {
        hasFailures: boolean;
        isPassing: boolean;
        hasErrors: boolean;
        hasActivationTasks: boolean;
        totalChecks: number;
        totalFailures: number;
        errors: CheckViolations;
        activationTasks: CheckViolations;
        warnings: CheckViolations;
        passes: Array<{ tag: string }>;
      };
    };
  };
};

type Params = {
  mode?: SIDEBAR_RIGHT_MODES;
  appId: string | number;
  fetchPolicy?: 'cache-and-network';
  isLazy?: boolean;
  onCompleted?: (data: QueryData) => void | {};
  onError?: () => void;
  version: SemVer;
};

export type ValidationResults = {
  errors: Array<ValidationResultSetWrapper>;
  warnings: Array<ValidationResultSetWrapper>;
  activation: Array<ValidationResultSetWrapper>;
  passes: Array<string>;
};

// This query gets everything from validation since it drives all other validation data in the app.
// All other consumers of validation data simply read from the cache.
const implementationValidationQuery = gql`
  fragment implementationViolationsFragment on ImplementationViolationsCheckWrapper {
    totalFailures
    results {
      type
      violations {
        type
        results {
          message
          tag
          deepLink
        }
      }
    }
  }

  query implementationValidationQuery(
    $id: ID!
    $version: String!
    $mode: String
  ) {
    platformIntegration(id: $id) {
      id
      implementation(version: $version) {
        id
        checks(mode: $mode) {
          hasFailures
          isPassing
          hasErrors
          hasActivationTasks
          totalChecks
          totalFailures
          errors {
            ...implementationViolationsFragment
          }
          activationTasks {
            ...implementationViolationsFragment
          }
          warnings {
            ...implementationViolationsFragment
          }
          passes {
            tag
          }
        }
      }
    }
  }
`;

export function normalizeVariables({
  appId,
  version,
  mode,
}: {
  appId: string | number;
  version: SemVer;
  mode?: SIDEBAR_RIGHT_MODES;
}) {
  const normalizedMode =
    mode === SIDEBAR_RIGHT_MODES.PUBLISHING
      ? 'activate'
      : mode === SIDEBAR_RIGHT_MODES.PROMOTING
      ? 'promote'
      : undefined;
  return {
    id: appId,
    version,
    mode: normalizedMode,
  };
}

export default function useValidationQuery({
  appId,
  version,
  mode,
  isLazy,
  onCompleted,
  onError,
  fetchPolicy,
}: Params) {
  // @ts-ignore
  const results = (isLazy ? useLazyQuery : useQuery)(
    implementationValidationQuery,
    {
      // So that useQuery re-renders when calling refetch/validate.
      // This gets us up-to-date loading state.
      notifyOnNetworkStatusChange: true,
      variables: normalizeVariables({ appId, version, mode }),
      onCompleted,
      onError,
      fetchPolicy,
    }
  );

  const validate = isLazy ? results[0] : noop;
  const { data, loading, refetch, error, called } = isLazy
    ? results[1]
    : results;
  const {
    errors = {},
    activationTasks = {},
    warnings = {},
    passes = [],
    ...helpers
  } = get(data, ['platformIntegration', 'implementation', 'checks'], {});

  return {
    ...helpers,
    apolloErrors: error,
    failedChecks: { errors, activationTasks, warnings },
    hasValidated: !loading && called,
    isValidating: loading,
    passes,
    // Wrapping refetch in case it's passed diretcly to an event handler
    // Otherwise, the handler passes DOM and event objects to refetch, which
    // Apollo assumes are options that lead to 💥
    validate: () => (isLazy ? validate() : refetch()),
  };
}
